/**
 * [ELICO] Cart drawer behaviour — 2026-07-28, revised 2026-07-29.
 *
 * Adds on top of Palo Alto:
 *   - variant swatch tray (one open at a time, keyboard + aria)
 *   - variant swap in cart, preserving quantity, properties and selling plan
 *   - subscription line <-> one-time purchase switch, with undo (changePlan)
 *   - quantity-tier swap (1 pakke -> 3 for 2) carrying the Appstle cadence across groups
 *   - quantity-break promo button (bump to the qty that triggers SK15 / BW15)
 *   - add-to-cart from nudges and the cross-sell row, applying the subscription code
 *   - debounced, optimistic quantity stepper
 *   - free-refill guard (see stripUnqualifiedDiscounts)
 *   - free-shipping progress bar
 *
 * IT COMPUTES NO PRICES. Every amount is rendered by Liquid from `cart`, which already
 * reflects Shopify's discounts — that is what keeps drawer price == checkout price. The
 * only number derived here is the shipping bar's remaining-to-spend, off the
 * server-rendered subtotal in [data-cart-total].
 *
 * Handlers are delegated from `document` so they survive cart-element.build() replacing
 * the drawer's innerHTML on every update.
 */
(function () {
  'use strict';

  var SEL = {
    trayToggle: '[data-elico-tray-toggle]',
    tray: '[data-elico-tray]',
    swap: '[data-elico-swap]',
    planSwitch: '[data-elico-plan-switch]',
    qtyPromo: '[data-elico-qty]',
    add: '[data-elico-add]',
    line: '[data-cart-item]',
    ship: '[data-elico-ship]',
    cartTotal: '[data-cart-total]',
    stepBtn: '[data-quantity-button]',
    discountChip: '[data-discount-body][data-discount-code]'
  };

  var routes = (window.theme && window.theme.routes) || {};
  // NOTE: the theme publishes `root`, not `root_url`. Reading the wrong key silently
  // fell back to '/', which pointed at the wrong locale root on shopelico.com/de|/nl.
  var ROOT = routes.root || '/';
  if (ROOT.slice(-1) !== '/') ROOT += '/';
  var CART_URL = routes.cart_url || '/cart';
  var ADD_URL = routes.cart_add_url || '/cart/add';
  var CHANGE_URL = routes.cart_change_url || '/cart/change';
  var UPDATE_URL = routes.cart_update_url || '/cart/update';

  var CONFIG = (function () {
    var node = document.querySelector('[data-elico-cart-strings]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return {};
    }
  })();

  var MANAGED_CODES = (CONFIG.managedCodes || []).map(function (c) {
    return String(c).toUpperCase();
  });

  // Hidden line property that remembers the selling plan a line had before it was switched
  // to a one-time purchase. Liquid renders the "Angre" notice from it (elico-cart-line.liquid).
  var PREV_PLAN_PROP = '_elico_prev_plan';

  var busy = false;
  var QTY_DEBOUNCE_MS = 450;

  /* ------------------------------------------------------------------ helpers */

  function t(key, fallback) {
    return CONFIG[key] || fallback;
  }

  function jsonPost(url, body) {
    return fetch(url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw data;
        return data;
      });
    });
  }

  function refreshCart() {
    clearStaleErrors();
    // The /cart page (sections/cart.liquid) does not re-render on theme:cart:refresh,
    // only the drawer does. Its lines keep stale keys, so a second scent swap posted a
    // removed key and /cart/change.js returned 400 "invalid id or line". Reload instead.
    if (document.body.classList.contains('template-cart')) {
      window.location.reload();
      return;
    }
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
  }

  /**
   * Palo Alto writes stock errors ("Beklager, det ser ut til at vi ikke har nok av
   * dette produktet") into [data-cart-errors] and never clears them, so the message
   * outlives the line that caused it — verified 2026-07-29 still on screen after the
   * cart had been emptied completely. Any successful cart mutation clears it; a real
   * error re-renders itself on the next response.
   */
  function clearStaleErrors() {
    document.querySelectorAll('.cart__errors, [data-cart-errors]').forEach(function (el) {
      el.textContent = '';
    });
  }

  function setBusy(state) {
    busy = state;
    document.querySelectorAll('.elico-cl, .elico-nudge, .elico-xs__card').forEach(function (el) {
      el.classList.toggle('is-busy', state);
    });
  }

  function toast(message) {
    if (!message) return;
    var el = document.querySelector('[data-elico-toast]');
    if (!el) {
      el = document.createElement('div');
      el.className = 'elico-toast';
      el.setAttribute('data-elico-toast', '');
      el.setAttribute('role', 'status');
      document.body.appendChild(el);
    }
    el.textContent = message;
    el.classList.add('is-visible');
    window.clearTimeout(el._t);
    el._t = window.setTimeout(function () {
      el.classList.remove('is-visible');
    }, 2200);
  }

  /** Line state published by elico-cart-line.liquid, so we never need to read the cart first. */
  function lineState(line) {
    var props = {};
    try {
      props = JSON.parse(line.getAttribute('data-line-props') || '{}');
    } catch (e) {
      props = {};
    }
    // Liquid's `| json` serialises a line's properties as an ARRAY OF PAIRS —
    // [["_source","cart-upsell-R1"]], and [] when empty — not as an object. Posting
    // that straight back is rejected ("expected Array to be a Hash: properties"), and
    // naively coercing it to {} silently drops the attribution property. Rebuild it.
    if (Array.isArray(props)) {
      var asObject = {};
      props.forEach(function (pair) {
        if (Array.isArray(pair) && pair.length === 2 && pair[0] != null) {
          asObject[pair[0]] = pair[1];
        }
      });
      props = asObject;
    }
    if (!props || typeof props !== 'object') props = {};
    return {
      key: line.getAttribute('data-line-key'),
      quantity: parseInt(line.getAttribute('data-line-qty'), 10) || 1,
      packs: parseInt(line.getAttribute('data-line-packs'), 10) || 1,
      variantId: line.getAttribute('data-line-variant'),
      sellingPlan: line.getAttribute('data-line-plan') || '',
      properties: props
    };
  }

  /* ------------------------------------------------------------------ discounts */

  /**
   * Discount codes currently on the cart.
   *
   * Read from [data-elico-applied-codes], which snippets/elico-cart-totals.liquid
   * renders from `cart.discount_codes`. This used to scrape the footer's discount
   * chips, which broke the moment those were narrowed to order-level discounts:
   * /cart/update.js REPLACES the code list, so an empty read meant adding the free
   * body wash refills silently removed the free hand soap refill.
   * The chips remain as a fallback for any surface that has not re-rendered yet.
   */
  function appliedCodes() {
    var source = document.querySelector('[data-elico-applied-codes]');
    if (source) {
      return (source.getAttribute('data-elico-applied-codes') || '')
        .split(',')
        .map(function (code) {
          return code.trim();
        })
        .filter(Boolean);
    }

    return Array.prototype.map
      .call(document.querySelectorAll(SEL.discountChip), function (el) {
        return el.getAttribute('data-discount-code');
      })
      .filter(Boolean);
  }

  function setCodes(codes) {
    return jsonPost(UPDATE_URL, { discount: codes.join(',') });
  }

  /**
   * Apply a discount code.
   *
   * Uses /cart/update.js rather than fetching /discount/CODE — the latter returns a full
   * HTML page (the theme's own discount UI uses the JSON endpoint for the same reason).
   * Existing codes are preserved so we never clobber one the customer entered.
   */
  function applyDiscountCode(code) {
    if (!code) return Promise.resolve();
    var codes = appliedCodes();
    if (
      codes.some(function (c) {
        return c.toUpperCase() === code.toUpperCase();
      })
    ) {
      return Promise.resolve();
    }
    codes.push(code);
    return setCodes(codes).catch(function () {});
  }

  /**
   * Free-refill guard.
   *
   * STARTERKIT-REFILL / BODYWASH-REFILL are subscription-only CODE discounts that take
   * the refill to 0 kr. They are not conditional on a starter kit being present, so a
   * customer could take the free refill and then delete the kit — leaving a 0 kr cart
   * with a free 4-pack. (Verified live 2026-07-28.)
   *
   * The real fix is the Discount Function in projects/shopify/free-refill-discount-function/,
   * which re-evaluates server-side and at checkout. Until that is deployed this strips any
   * code WE auto-applied once the cart holds nothing that ships a bottle. Client-side, so
   * it is a speed bump, not a lock — do not treat it as the fix.
   */
  function stripUnqualifiedDiscounts() {
    if (!MANAGED_CODES.length) return;

    var codes = appliedCodes();
    var managedOnCart = codes.filter(function (c) {
      return MANAGED_CODES.indexOf(c.toUpperCase()) !== -1;
    });
    if (!managedOnCart.length) return;

    var qualifies = document.querySelector(
      '[data-line-role="dispenser"], [data-line-role="bundle"]'
    );
    if (qualifies) return;

    var remaining = codes.filter(function (c) {
      return MANAGED_CODES.indexOf(c.toUpperCase()) === -1;
    });
    setCodes(remaining)
      .then(refreshCart)
      .catch(function () {});
  }

  /* --------------------------------------------------------------- swatch tray */

  function closeTrays(except) {
    document.querySelectorAll(SEL.tray).forEach(function (tray) {
      if (tray === except) return;
      tray.hidden = true;
    });
    document.querySelectorAll(SEL.trayToggle).forEach(function (btn) {
      if (except && btn.getAttribute('aria-controls') === except.id) return;
      btn.setAttribute('aria-expanded', 'false');
      btn.classList.remove('is-open');
    });
  }

  function toggleTray(btn) {
    var tray = document.getElementById(btn.getAttribute('aria-controls'));
    if (!tray) return;

    var willOpen = tray.hidden;
    closeTrays(willOpen ? tray : null);

    tray.hidden = !willOpen;
    btn.setAttribute('aria-expanded', String(willOpen));
    btn.classList.toggle('is-open', willOpen);

    if (willOpen) {
      var active = tray.querySelector('.elico-sw.is-active') || tray.querySelector('.elico-sw');
      if (active) active.focus();
    }
  }

  /* ------------------------------------------------------------- cart mutation */

  /**
   * Swap a cart line to a different variant.
   *
   * Cart AJAX cannot change a line's variant in place, so this removes the line and
   * re-adds the target with the same quantity, properties and equivalent selling plan
   * (resolved in Liquid by matching plan NAME, so cadence survives moving between
   * Appstle groups). Line state comes from data attributes — no pre-read round trip.
   */
  function swapVariant(line, variantId, sellingPlan) {
    var state = lineState(line);
    if (!state.key) return Promise.resolve();

    setBusy(true);

    // The one-time-switch marker only means something while a line has no plan, so a line
    // that is (re)added WITH a plan drops it (it would otherwise stop the line merging).
    var props = {};
    Object.keys(state.properties).forEach(function (name) {
      if (sellingPlan && name === PREV_PLAN_PROP) return;
      props[name] = state.properties[name];
    });

    var payload = { id: Number(variantId), quantity: state.quantity };
    if (Object.keys(props).length) payload.properties = props;
    if (sellingPlan) payload.selling_plan = Number(sellingPlan);

    // Remove first so a sold-out target cannot leave the cart holding both lines.
    return jsonPost(CHANGE_URL, { id: state.key, quantity: 0 })
      .then(function () {
        return jsonPost(ADD_URL, { items: [payload] }).catch(function (err) {
          // Put the original back rather than silently dropping the customer's item.
          // This path exists precisely because something went wrong, so it retries bare
          // (id + quantity only) if the full restore also fails — losing the line is a
          // far worse outcome than losing a line property.
          var restore = { id: Number(state.variantId), quantity: state.quantity };
          if (Object.keys(state.properties).length) restore.properties = state.properties;
          if (state.sellingPlan) restore.selling_plan = Number(state.sellingPlan);

          return jsonPost(ADD_URL, { items: [restore] })
            .catch(function () {
              return jsonPost(ADD_URL, {
                items: [{ id: Number(state.variantId), quantity: state.quantity }]
              });
            })
            .then(function () {
              throw err;
            });
        });
      })
      .then(refreshCart)
      .catch(function (err) {
        toast((err && err.description) || t('error', 'Noe gikk galt. Prøv igjen.'));
        refreshCart();
      })
      .then(function () {
        setBusy(false);
      });
  }

  /**
   * Switch a cart line between a subscription and a one-time purchase.
   *
   * mode 'onetime': drop the selling plan in place (/cart/change with selling_plan null) and
   *   park the old plan id in the hidden PREV_PLAN_PROP line property, so the switch can be
   *   undone. A failure leaves the line exactly as it was.
   * mode 'restore' (the "Angre" link): put restorePlan back. /cart/change cannot REMOVE a
   *   line property (an empty or null value is stored as-is and stops the line merging with
   *   an identical one), so this goes through swapVariant: remove, then re-add the same
   *   variant with the plan and without the marker.
   *
   * Same shape as swapVariant: line state from data attributes, busy state, re-render, toast
   * on error.
   */
  function changePlan(line, mode, restorePlan) {
    var state = lineState(line);
    if (!state.key) return Promise.resolve();

    if (mode === 'restore') {
      if (!Number(restorePlan)) return Promise.resolve();
      return swapVariant(line, Number(state.variantId), restorePlan);
    }

    if (!state.sellingPlan) return Promise.resolve();

    var props = {};
    Object.keys(state.properties).forEach(function (name) {
      props[name] = state.properties[name];
    });
    props[PREV_PLAN_PROP] = String(state.sellingPlan);

    setBusy(true);

    return jsonPost(CHANGE_URL, {
      id: state.key,
      quantity: state.quantity,
      selling_plan: null,
      properties: props
    })
      .then(refreshCart)
      .catch(function (err) {
        toast((err && (err.description || err.message)) || t('error', 'Noe gikk galt. Prøv igjen.'));
        refreshCart();
      })
      .then(function () {
        setBusy(false);
      });
  }

  /**
   * `bundle` is an optional "variantId:sellingPlanId,variantId:sellingPlanId" string
   * from the nudge's data-bundle attribute. Body wash's free offer is two refills,
   * one of each scent, so one tap has to add two different VARIANTS — quantity 2 of
   * one scent is the wrong thing. Everything goes in a single /cart/add.js call, so
   * the drawer still re-renders once.
   */
  function addVariant(variantId, sellingPlan, source, title, discountCode, bundle) {
    setBusy(true);

    // Underscore prefix hides this from the customer while making per-rule attach rate
    // measurable in order data later.
    var props = source ? { _source: source } : null;
    var items = [];

    (bundle || '').split(',').forEach(function (pair) {
      var parts = pair.split(':');
      var id = Number(parts[0]);
      if (!id) return;
      var entry = { id: id, quantity: 1 };
      var plan = Number(parts[1]);
      if (plan) entry.selling_plan = plan;
      if (props) entry.properties = props;
      items.push(entry);
    });

    if (!items.length) {
      var item = { id: Number(variantId), quantity: 1 };
      if (sellingPlan) item.selling_plan = Number(sellingPlan);
      if (props) item.properties = props;
      items.push(item);
    }

    return jsonPost(ADD_URL, { items: items })
      .then(function () {
        return applyDiscountCode(discountCode);
      })
      .then(function () {
        if (title) toast(title + ' ' + t('added', 'lagt til'));
        refreshCart();
      })
      .catch(function (err) {
        toast((err && err.description) || t('error', 'Noe gikk galt. Prøv igjen.'));
      })
      .then(function () {
        setBusy(false);
      });
  }

  /* ------------------------------------------------- debounced quantity stepper */

  /**
   * Palo Alto fires a /cart/change per stepper click with no debounce, and the drawer
   * re-renders on each response — so rapid clicks hit elements that get replaced and the
   * increments are lost. Measured: four "+" clicks from qty 1 ended at qty 2.
   *
   * This intercepts the buttons in the capture phase, moves the number optimistically,
   * and sends ONE /cart/change with the final quantity. Also cuts four round trips to one.
   */
  var pending = null;

  function displayFor(line, quantity) {
    var packs = lineState(line).packs;
    return quantity * (packs || 1);
  }

  function paintQuantity(line, quantity) {
    var display = line.querySelector('.elico-cl__qty-display');
    var field = line.querySelector('[data-quantity-field]');
    if (field) field.value = quantity;
    if (display) display.textContent = displayFor(line, quantity);
    updateHeaderCount();
  }

  function queueQuantity(line, quantity) {
    if (pending && pending.line !== line) flushQuantity();

    if (quantity <= 0) {
      // Removal is immediate — nothing to batch, and it changes line indices.
      window.clearTimeout(pending && pending.timer);
      pending = null;
      var removeLink = line.querySelector('[data-item-remove]');
      if (removeLink) removeLink.click();
      return;
    }

    paintQuantity(line, quantity);

    if (pending) window.clearTimeout(pending.timer);
    pending = { line: line, quantity: quantity, timer: null };
    pending.timer = window.setTimeout(flushQuantity, QTY_DEBOUNCE_MS);
  }

  function flushQuantity() {
    if (!pending) return;
    var line = pending.line;
    var quantity = pending.quantity;
    var field = line.querySelector('[data-quantity-field]');
    pending = null;
    if (!field) return;

    line.dispatchEvent(
      new CustomEvent('theme:cart:update', {
        bubbles: true,
        detail: { id: field.getAttribute('data-id'), quantity: quantity }
      })
    );
  }

  /** Bump a line to the quantity that triggers a live quantity-break discount. */
  function setLineQuantity(line, quantity) {
    paintQuantity(line, quantity);
    if (pending) window.clearTimeout(pending.timer);
    pending = { line: line, quantity: quantity, timer: null };
    flushQuantity();
  }

  /* --------------------------------------------------------------- header count */

  /**
   * theme.js counts line items, so a "3 for 2" bundle read as 1 while the stepper showed
   * 3. Recount from what the drawer actually displays.
   */
  function updateHeaderCount() {
    var total = 0;
    document.querySelectorAll(SEL.line).forEach(function (line) {
      var display = line.querySelector('.elico-cl__qty-display');
      var field = line.querySelector('[data-quantity-field]');
      total += parseInt(display ? display.textContent : field && field.value, 10) || 0;
    });

    // Both plural forms come from locales/, so the count reads correctly in every
    // language; swap the number into the plural form for anything above one.
    var one = CONFIG.itemCountOne;
    var other = CONFIG.itemCountOther;
    var label;
    if (total === 1 && one) {
      label = one;
    } else if (other) {
      label = other.replace(/\d+/, total);
    } else {
      label = String(total);
    }

    document.querySelectorAll('[data-cart-items-qty]').forEach(function (el) {
      el.textContent = label;
    });
  }

  /* ------------------------------------------------------ free-shipping progress */

  /**
   * 2026-09-22 fix ("Fri frakt låst opp" at 678 kr with a 699 kr threshold): the bar is
   * rendered once and never re-rendered, and this used to run only on theme:cart:added,
   * which can fire before cart-element.build() swaps in the new totals — the bar then
   * froze on an earlier, higher cart. Now it (a) reads total AND limit from the totals
   * node that build() refreshes every cycle, scoped to the bar's own cart container so
   * the /cart page and the drawer never read each other's node, and (b) also re-runs
   * whenever that node changes (observer below).
   */
  function findTotalFor(bar) {
    var scope = bar.closest('[data-cart-drawer], cart-drawer, .cart-drawer, form, section');
    var el = scope ? scope.querySelector(SEL.cartTotal) : null;
    return el || document.querySelector(SEL.cartTotal);
  }

  function updateShippingBar() {
    document.querySelectorAll(SEL.ship).forEach(updateOneShippingBar);
  }

  function updateOneShippingBar(bar) {
    var totalEl = findTotalFor(bar);
    if (!totalEl) return;

    var subtotal = parseInt(totalEl.getAttribute('data-cart-total'), 10);
    var limit = parseInt(totalEl.getAttribute('data-elico-ship-limit-fresh'), 10);
    if (isNaN(limit) || limit <= 0) limit = parseInt(bar.getAttribute('data-elico-ship-limit'), 10);
    if (isNaN(subtotal) || isNaN(limit) || limit <= 0) return;

    var remaining = limit - subtotal;
    var unlocked = remaining <= 0;
    var percent = Math.min(100, Math.round((subtotal / limit) * 100));

    var fill = bar.querySelector('[data-elico-ship-fill]');
    if (fill) fill.style.width = percent + '%';

    bar.classList.toggle('is-unlocked', unlocked);
    bar.classList.toggle('is-empty', subtotal === 0);

    var msg = bar.querySelector('[data-elico-ship-msg]');
    if (!msg) return;
    var pill = msg.querySelector('.elico-ship__pill');
    var label = msg.querySelector('[data-elico-ship-label]');
    if (!pill || !label) return;

    if (unlocked) {
      pill.textContent = pill.getAttribute('data-unlocked-pill') || pill.textContent;
      label.textContent = label.getAttribute('data-unlocked-label') || label.textContent;
    } else {
      var money =
        window.theme && window.theme.formatMoney
          ? window.theme.formatMoney(remaining, window.theme.moneyFormat)
          : String(remaining / 100);
      // This shop's money format wraps the amount in <span class=money>; the pill is set
      // with textContent, so strip the markup rather than print it literally.
      money = String(money).replace(/<[^>]*>/g, '');
      // Drop zero decimals wherever the currency suffix sits ("300,00 kr" -> "300 kr" on
      // elico.se / elicocare.dk; the old endsWith check only caught "300,00").
      money = money.replace(/[.,]00(?=\D*$)/, '');
      pill.textContent = money;
      label.textContent = label.getAttribute('data-remaining-label') || label.textContent;
    }
  }

  /* -------------------------------------------------------------------- binding */

  // Capture phase so this runs before Palo Alto's quantity-counter and can replace it.
  document.addEventListener(
    'click',
    function (event) {
      var btn = event.target.closest && event.target.closest(SEL.stepBtn);
      if (!btn) return;
      var line = btn.closest('.elico-cl');
      if (!line) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      var field = line.querySelector('[data-quantity-field]');
      if (!field) return;

      var current = pending && pending.line === line ? pending.quantity : parseInt(field.value, 10) || 0;
      var isPlus = btn.hasAttribute('data-quantity-plus');
      var max = parseInt(field.getAttribute('data-quantity-max'), 10);
      var next = isPlus ? current + 1 : current - 1;
      if (isPlus && !isNaN(max) && max > 0 && next > max) return;

      queueQuantity(line, next);
    },
    true
  );

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var trayToggle = target.closest(SEL.trayToggle);
    if (trayToggle) {
      event.preventDefault();
      toggleTray(trayToggle);
      return;
    }

    if (busy && target.closest(SEL.swap + ',' + SEL.add + ',' + SEL.qtyPromo + ',' + SEL.planSwitch)) {
      event.preventDefault();
      return;
    }

    var planSwitch = target.closest(SEL.planSwitch);
    if (planSwitch) {
      event.preventDefault();
      var planLine = planSwitch.closest(SEL.line);
      if (planLine) {
        changePlan(
          planLine,
          planSwitch.getAttribute('data-elico-plan-switch'),
          planSwitch.getAttribute('data-restore-plan')
        );
      }
      return;
    }

    var swap = target.closest(SEL.swap);
    if (swap) {
      event.preventDefault();
      var line = swap.closest(SEL.line);
      if (line) {
        swapVariant(line, Number(swap.getAttribute('data-variant-id')), swap.getAttribute('data-selling-plan'));
      }
      return;
    }

    var qtyBtn = target.closest(SEL.qtyPromo);
    if (qtyBtn) {
      event.preventDefault();
      var qtyLine = qtyBtn.closest(SEL.line);
      if (qtyLine) setLineQuantity(qtyLine, parseInt(qtyBtn.getAttribute('data-target-qty'), 10));
      return;
    }

    var addBtn = target.closest(SEL.add);
    if (addBtn) {
      event.preventDefault();
      addVariant(
        addBtn.getAttribute('data-variant-id'),
        addBtn.getAttribute('data-selling-plan'),
        addBtn.getAttribute('data-source'),
        addBtn.getAttribute('data-title'),
        addBtn.getAttribute('data-discount-code'),
        addBtn.getAttribute('data-bundle')
      );
    }
  });

  document.addEventListener(
    'keydown',
    function (event) {
      if (event.key !== 'Escape') return;
      var open = document.querySelector(SEL.tray + ':not([hidden])');
      if (!open) return;
      // Swallow it so the drawer stays open — closing the tray is the more specific action.
      event.stopPropagation();
      var toggle = document.querySelector('[aria-controls="' + open.id + '"]');
      closeTrays(null);
      if (toggle) toggle.focus();
    },
    true
  );

  // Send any queued quantity before the customer leaves for checkout.
  document.addEventListener('submit', flushQuantity, true);
  window.addEventListener('pagehide', flushQuantity);

  // Fires after cart-element.build() swaps in freshly rendered lines, upsells and totals.
  // The bar update runs first; the observer below catches the case where this event
  // wins the race against the DOM swap.
  document.addEventListener('theme:cart:added', function () {
    updateShippingBar();
    closeTrays(null);
    updateHeaderCount();
    stripUnqualifiedDiscounts();
  });

  document.addEventListener('theme:cart:loaded', function () {
    observePriceHolders();
    updateShippingBar();
    updateHeaderCount();
  });

  /**
   * Re-run the bar after EVERY build(), not only after events: build() replaces the
   * innerHTML of [data-cart-price-holder] (quantity change, remove, variant swap, nudge
   * add all go through it). One animation frame of batching, no extra requests.
   */
  var shipFrame = 0;
  function scheduleShippingBar() {
    if (shipFrame) return;
    shipFrame = window.requestAnimationFrame(function () {
      shipFrame = 0;
      updateShippingBar();
    });
  }

  function observePriceHolders() {
    if (!window.MutationObserver) return;
    document.querySelectorAll('[data-cart-price-holder]').forEach(function (holder) {
      if (holder._elicoShipObserved) return;
      holder._elicoShipObserved = true;
      new MutationObserver(scheduleShippingBar).observe(holder, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-cart-total', 'data-elico-ship-limit-fresh']
      });
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    observePriceHolders();
    updateShippingBar();
    updateHeaderCount();
  });
  if (document.readyState !== 'loading') observePriceHolders();
})();
